import React from 'react';
import { Redirect } from 'react-router-dom';

import * as Yup from 'yup';
import { Form, Formik, FormikValues } from 'formik';

import Button from '../../components/buttons/basicButton';
import AuthError from '../../components/errors/AuthError';
import PasswordField from '../../components/inputs/PasswordField';
import TextInput from '../../components/inputs/textInput';
import NavLink from '../../components/nav/NavLink';
import styled from 'styled-components';

import aampeTheme from '../../constants/aampeTheme';

import { BaseError } from '../../helpers/BaseError';

// hooks
import useResetPasswordMutation from '../../hooks/auth/useResetPasswordMutation';
import useFromLocationState from '../../hooks/helper/useFromLocationState';
import useQueryParams from '../../hooks/helper/useQueryParams';

import envConfig from '../../services/env-config';

// components
import BaseCard from './baseCard';
// helper
import { MIN_PASSWORD_LENGTH, PASSWORD_REGEX } from './helper';

const formSchema = Yup.object({
    email: Yup.string().email('Must be a valid email').required('Required'),
    password: Yup.string()
        .required('Required')
        .min(MIN_PASSWORD_LENGTH, '8 chars minimum')
        .matches(PASSWORD_REGEX),
    passwordConfirmation: Yup.string().oneOf(
        [Yup.ref('password'), null],
        'Passwords must match',
    ),
});

export default function ResetCard() {
    // history
    const resetMutation = useResetPasswordMutation();
    const prevLocation = useFromLocationState();
    const { link } = useQueryParams();

    const handleSubmit = (values: FormikValues) => {
        resetMutation.mutate({
            email: values.email,
            password: values.password,
            token: link,
        });
    };

    if (!link || !link.length) {
        return <Redirect to={prevLocation.pathname} />;
    }

    return (
        <BaseCard
            title={resetMutation.isSuccess ? 'Success!' : 'Password Reset'}
        >
            <FormContainer>
                {resetMutation.isError && (
                    <AuthError
                        errorMsg={
                            resetMutation.error instanceof BaseError
                                ? resetMutation.error.message
                                : ''
                        }
                    />
                )}
                {!resetMutation.isSuccess && (
                    <Formik
                        initialValues={{
                            email: '',
                            password: '',
                            passwordConfirmation: '',
                        }}
                        validationSchema={formSchema}
                        onSubmit={handleSubmit}
                    >
                        {({
                            values,
                            handleChange,
                            handleBlur,
                            errors,
                            touched,
                        }) => (
                            <Form>
                                <TextInput
                                    label="Email:"
                                    name="email"
                                    placeholder="email@domain.com"
                                    value={values?.email}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors?.email}
                                    touched={touched?.email}
                                    inputStyle={{
                                        width: '100%',
                                    }}
                                    wrapperStyle={{
                                        marginBottom: aampeTheme.spacing(1),
                                    }}
                                />

                                <PasswordField
                                    label="New password:"
                                    name="password"
                                    value={values.password}
                                    handleChange={handleChange}
                                    handleBlur={handleBlur}
                                    touched={touched.passwordConfirmation}
                                />
                                <PasswordField
                                    label="Confirm password:"
                                    name="passwordConfirmation"
                                    value={values.passwordConfirmation}
                                    handleChange={handleChange}
                                    handleBlur={handleBlur}
                                    touched={touched.passwordConfirmation}
                                    error={errors.passwordConfirmation}
                                    showValidationCheckList={false}
                                />
                                <StyledButton
                                    name="Reset password"
                                    type="submit"
                                    isPrimary
                                    isDisabled={resetMutation.isPending}
                                    isLoading={resetMutation.isPending}
                                    isSuccess={resetMutation.isSuccess}
                                />
                            </Form>
                        )}
                    </Formik>
                )}
                <Footer>
                    {resetMutation.isSuccess
                        ? 'Your password was successfully reset.'
                        : "Don't need to reset?"}
                    <Options>
                        <StyledNavLink
                            text="Login"
                            destination={envConfig.AUTH_SERVICE_SIGNIN}
                        />
                    </Options>
                </Footer>
            </FormContainer>
        </BaseCard>
    );
}

const FormContainer = styled.div({
    width: '100%',
});

const StyledNavLink = styled(NavLink)`
    font-size: 16px;
`;

const StyledButton = styled(Button)`
    width: 100%;
    margin: ${aampeTheme.spacing(2, 0)};
    font-size: 16px;
    font-weight: 600;
`;

const Footer = styled.div({
    display: 'flex',
    flexFlow: 'column nowrap',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    marginTop: aampeTheme.spacing(1),
});

const Options = styled.div({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: aampeTheme.spacing(1),
    marginTop: aampeTheme.spacing(2),
});
